// Categoria di servizio di un viaggio giornaliero: *perché* la persona viaggia
// (centro diurno, scuola, terapie…).
//
// NB: è un terzo classificatore, distinto dai due che esistono già e con cui
// non va confuso:
//   - `service_mode`  → COME si svolge il servizio (trasporto, ambulanza, treno);
//   - `trip_type`     → COSA si trasporta (persone, oggetti), enum del form viaggi;
//   - `service_category` → DOVE/PERCHÉ si va, questo file.
// Il campo dati è `service_category` (inglese, come da convenzione sui campi
// nuovi); le sigle italiane dei fogli operativi restano solo come sinonimi in
// ingresso.

export const SERVICE_CATEGORIES = {
  DAY_CENTER: 'day_center',
  SCHOOL: 'school',
  THERAPY: 'therapy',
  WORK: 'work',
  HOSPITAL: 'hospital',
  OTHER: 'other',
};

// `synonyms` raccoglie le sigle viste nei tracciati operativi. Vanno confrontate
// normalizzate (minuscole, senza accenti/punteggiatura).
export const SERVICE_CATEGORY_META = {
  [SERVICE_CATEGORIES.DAY_CENTER]: {
    label: 'Centro diurno',
    variant: 'teal',
    icon: 'house-medical',
    synonyms: ['cdd', 'cd', 'cse', 'sfa', 'centro diurno', 'centrodiurno', 'diurno'],
  },
  [SERVICE_CATEGORIES.SCHOOL]: {
    label: 'Scuola',
    variant: 'info',
    icon: 'school',
    synonyms: ['scuola', 'scolastico', 'school', 'sc'],
  },
  [SERVICE_CATEGORIES.THERAPY]: {
    label: 'Terapie',
    variant: 'purple',
    icon: 'hand-holding-medical',
    synonyms: ['ter', 'terapia', 'terapie', 'fkt', 'riabilitazione', 'therapy'],
  },
  [SERVICE_CATEGORIES.WORK]: {
    label: 'Lavoro',
    variant: 'warning',
    icon: 'briefcase',
    synonyms: ['lav', 'lavoro', 'work', 'tirocinio'],
  },
  [SERVICE_CATEGORIES.HOSPITAL]: {
    label: 'Ospedale',
    variant: 'danger',
    icon: 'hospital',
    synonyms: ['osp', 'ospedale', 'visita', 'visite', 'hospital', 'ambulatorio'],
  },
  [SERVICE_CATEGORIES.OTHER]: {
    label: 'Altro',
    variant: 'neutral',
    icon: 'ellipsis',
    synonyms: ['altro', 'other', 'varie'],
  },
};

const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Indice sinonimo → categoria, costruito una volta sola.
//
// Una `Map` sovrascrive in silenzio: se due categorie dichiarassero lo stesso
// sinonimo (una volta normalizzato), l'ultima vincerebbe e le sigle di quella
// prima verrebbero riclassificate senza alcun segnale. È un errore di registro,
// non di dati, quindi si segnala in sviluppo e si tiene il **primo** binding —
// l'ordine del registro resta la fonte di verità e l'aggiunta sbagliata non
// cambia la classificazione già in produzione.
const BY_SYNONYM = new Map();

const registerSynonym = (raw, key) => {
  const norm = normalize(raw);
  if (!norm) return;
  const existing = BY_SYNONYM.get(norm);
  if (existing !== undefined) {
    // Stesso sinonimo per la stessa categoria (es. label che coincide con un
    // sinonimo) è innocuo: solo le collisioni fra categorie diverse contano.
    if (existing !== key && import.meta.env?.DEV) {
      console.error(
        `[serviceCategories] sinonimo duplicato "${raw}" (normalizzato "${norm}"): ` +
          `già mappato su "${existing}", ignorato per "${key}".`
      );
    }
    return;
  }
  BY_SYNONYM.set(norm, key);
};

for (const [key, meta] of Object.entries(SERVICE_CATEGORY_META)) {
  registerSynonym(key, key);
  registerSynonym(meta.label, key);
  for (const syn of meta.synonyms) registerSynonym(syn, key);
}

/**
 * Riconduce la sigla di un tracciato a una categoria nota.
 *
 * Un valore non riconosciuto NON diventa `other`: torna slugificato così com'è.
 * Accorpare l'ignoto al catch-all cancellerebbe l'informazione proprio dove
 * serve — è dalle sigle sconosciute che si capisce quali categorie mancano.
 *
 * @param {string} value
 * @returns {string|null} chiave canonica, slug del valore originale, o null se vuoto.
 */
export function normalizeServiceCategory(value) {
  const norm = normalize(value);
  if (!norm) return null;
  return BY_SYNONYM.get(norm) ?? norm.replace(/ /g, '_');
}

/** True se il valore è una delle categorie canoniche. */
export const isKnownServiceCategory = (value) =>
  Object.prototype.hasOwnProperty.call(SERVICE_CATEGORY_META, value);

/** Etichetta leggibile; per una categoria sconosciuta torna il valore stesso. */
export const getServiceCategoryLabel = (value) =>
  SERVICE_CATEGORY_META[value]?.label ?? (value ? String(value) : '—');

export const SERVICE_CATEGORY_OPTIONS = Object.entries(SERVICE_CATEGORY_META).map(
  ([value, meta]) => ({ value, label: meta.label })
);

const CANONICAL_ORDER = Object.keys(SERVICE_CATEGORY_META);

/**
 * Opzioni di filtro costruite sui dati davvero caricati.
 *
 * Non si usa `SERVICE_CATEGORY_OPTIONS` fisso per due motivi opposti e
 * simmetrici: le categorie canoniche assenti dalla lista sarebbero voci che non
 * filtrano niente, e le sigle **sconosciute** — che `normalizeServiceCategory`
 * di proposito non accorpa su `other` — sparirebbero dal filtro proprio dove
 * servono, cioè dove si scopre che al registro manca una sigla.
 *
 * Ordine: prima le canoniche nell'ordine del registro, poi le ignote in ordine
 * alfabetico.
 */
export function buildServiceCategoryOptions(rows = [], field = 'service_category') {
  const present = new Set();
  for (const row of rows) {
    const value = row?.[field];
    // Solo null/undefined/stringa vuota si scartano: un valore falsy ma
    // presente nei dati (`0`, `false`) resta una categoria filtrabile.
    if (value != null && value !== '') present.add(String(value));
  }

  const known = CANONICAL_ORDER.filter((key) => present.has(key));
  const unknown = [...present].filter((v) => !isKnownServiceCategory(v)).sort();

  return [...known, ...unknown].map((value) => ({
    value,
    label: getServiceCategoryLabel(value),
  }));
}
